import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "Common/Globle/Spinner";
import { validateArrayForm, validateField } from "utils/validation";
import {
    ReservationDetailsRules,
    RoomFieldsRules,
    AdvanceDetailsRules,
    DormitoryRules
} from "validation/ValidationRules";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import {
    createRoomCheckIn,
    getAllPaymentMethods,
    getAllRoomTypes,
    getChargePercent
} from 'adminApi/RoomCheckInApi/RoomCheckIn';
import CreateRoomCheckInForm from "./Components/CreateRoomCheckInForm";
import useFetchAllData from "../../Components/useFetchAllData";
import { getAllowedFieldsByType, validateAdvanceRow } from "../../HELPERS/Helpers"

// IMPORT YOUR FIXED JSON AS-IS
import fields from "../json/RoomCheckInFields.json";

const { form_data } = fields;

const {
    Reservation_Details,
    RoomFields,
    extraFields,
    Advance_Details,
    Rent_Info,
    parkingFields
} = form_data

// Build flat form fields as empty strings
const data = [
    ...Reservation_Details,
    ...parkingFields
]

const roomFields = [
    ...RoomFields,
    ...extraFields,
    ...Rent_Info
]

// Convert field name arrays to { fieldName: "" } objects
const flatInitialValues = Object.fromEntries(
    data.map((f) => [
        f.name,
        f.name === "checkin_date" ? new Date().toISOString().split("T")[0] : f.name === "total_vehicle" ? 0
            : ""
    ])
);

// Convert room field names to one empty room object
const emptyRoom = {
    ...Object.fromEntries(roomFields.map((f) => [f.name, ""])),
    capacity: "",
    accommodation_type: "ROOM",
    complementary_type: "",
    avalible_beds: "",
    parking_rate: "",
    Unic_key: crypto.randomUUID(),
};

// next to emptyRoom
const emptyPaymentDetail = {
    ...Object.fromEntries(Advance_Details.map((f) => [f.name, ""])),
    Unic_key: crypto.randomUUID(),
};

const initialValues = {
    ...flatInitialValues,
    customer_id: "",
    full_guest_name: "",
    country_dial_code: "",
    mobile: "",
    is_apply_balance: false,
    avalible_balance: "",
    is_apply_balance_amount: "",
    rooms: [emptyRoom],
}

const CreateRoomCheckIn = () => {

    const navigate = useNavigate();
    const [ChildrenMinAge, setChildrenMinAge] = useState(null);
    const [ChildrenMaxAge, setChildrenMaxAge] = useState(null);

    // Dropdown data
    const {
        data: roomTypesOption = []
    } = useFetchAllData(
        {
            apiCall: getAllRoomTypes
        }
    );


    const {
        data: paymentMethodTypesOption = []
    } = useFetchAllData(
        {
            apiCall: getAllPaymentMethods
        }
    );

    const {
        data: taxConfig
    } = useFetchAllData(
        {
            apiCall: getChargePercent
        }
    );

    // KEEP JSON EXACT — JUST CLONE IT
    const clonedInitialValues = {
        ...initialValues,
        rooms: [{ ...initialValues.rooms[0] }],
        PaymentDetail: [{ ...emptyPaymentDetail }],
    };

    const {
        values,
        setValues,
        errors,
        handleChange,
        setErrors
    } =
        useFormChange(
            clonedInitialValues,
            ReservationDetailsRules
        );

    const FindRool = (rooms) => {
        const roomRules = {};

        rooms.forEach((room, index) => {
            roomRules[index] = {
                ...(room.accommodation_type === "ROOM"
                    ? RoomFieldsRules
                    : DormitoryRules)
            };
        });

        return roomRules;
    };


    const { handleSubmit, loading: loadingSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();

            // Exclude complex fields
            const excludedFields = [
                "rooms",
                "PaymentDetail",
                "tax_details",
                "vehicle_numbers",
            ];

            Object.entries(formValues).forEach(([key, value]) => {
                if (excludedFields.includes(key)) return;

                if (key === "guest_id") {
                    formData.append("guest_id", JSON.stringify(value || []));
                } else {
                    formData.append(key, value ?? "");
                }
            });

            // Allowed room fields by accommodation type
            const allowedByType = getAllowedFieldsByType(form_data);

            const cleanedRooms = formValues.rooms.map((room) => {
                const allowedFields = new Set([
                    ...(allowedByType[room.accommodation_type] || []),
                    "accommodation_type",
                    "complementary_price_type",
                ]);

                const roomWithExtras = {
                    ...room,
                    extracheck_in_date:
                        room.extracheck_in_date ||
                        formValues.checkin_date ||
                        "",
                    extracheck_out_date:
                        room.extracheck_out_date ||
                        formValues.checkout_date ||
                        "",
                };

                return Object.fromEntries(
                    Object.entries(roomWithExtras).filter(([key]) =>
                        allowedFields.has(key)
                    )
                );
            });

            // Remove frontend-only key
            const paymentDetail = (formValues.PaymentDetail || []).map(
                ({ Unic_key, ...rest }) => rest
            );

            const tax_details = (formValues.tax_details || []).map(
                ({ Unic_key, ...rest }) => rest
            );

            formData.append("rooms", JSON.stringify(cleanedRooms));
            formData.append("PaymentDetail", JSON.stringify(paymentDetail));
            formData.append("tax_details", JSON.stringify(tax_details));
            formData.append(
                "vehicle_numbers",
                JSON.stringify(formValues.vehicle_numbers || [])
            );

            return createRoomCheckIn({ data: formData });
        },

        (vals) => {
            const allrules = {
                ...ReservationDetailsRules,
                rooms: FindRool(vals.rooms),
                PaymentDetail: {
                    ...AdvanceDetailsRules,
                },
            };

            const validationErrors = validateArrayForm(vals, allrules);

            // Custom validation for payment rows
            const advanceRowErrors = (vals.PaymentDetail || []).map((row) =>
                validateAdvanceRow(row, paymentMethodTypesOption)
            );

            const hasAnyAdvanceError = advanceRowErrors.some(
                (e) => Object.keys(e).length > 0
            );

            if (hasAnyAdvanceError) {
                validationErrors.PaymentDetail = (vals.PaymentDetail || []).map(
                    (_, index) => ({
                        ...(validationErrors.PaymentDetail?.[index] || {}),
                        ...advanceRowErrors[index],
                    })
                );
            }

            setErrors(validationErrors);

            return validationErrors;
        },

        () => navigate(-1),

        "Room Check In Created Successfully"
    );


    return (
        <>
            {loadingSubmit && <Spinner isSpinner={true} />}

            {(
                <CreateRoomCheckInForm
                    values={values}
                    setValues={setValues}
                    errors={errors}
                    handleChange={handleChange}
                    handleSubmit={(e) => handleSubmit(e, values)}
                    loading={loadingSubmit}
                    roomTypesOption={roomTypesOption}
                    paymentMethodTypesOption={paymentMethodTypesOption}
                    taxConfig={taxConfig}
                    initialValues={initialValues}
                    form_data={form_data}
                    ChildrenMinAge={ChildrenMinAge}
                    ChildrenMaxAge={ChildrenMaxAge}
                    emptyRoom={emptyRoom}
                    roomFields={roomFields}
                    setChildrenMinAge={setChildrenMinAge}
                    setChildrenMaxAge={setChildrenMaxAge}
                    emptyPaymentDetail={emptyPaymentDetail}
                    setErrors={setErrors}
                    validateField={validateField}
                    DormitoryRules={DormitoryRules}
                    RoomFieldsRules={RoomFieldsRules}
                    AdvanceDetailsRules={AdvanceDetailsRules}
                />
            )}
        </>
    );
};

export default CreateRoomCheckIn;